import React, { useEffect } from "react";
import {
	Link,
	useLoaderData,
	useRouteError,
	useSearchParams,
} from "@remix-run/react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import LoadingIndicator from "~/components/loading-indicator";
import { isLoadingAtom } from "~/lib/jotai/loading";
import { emptyUser, User, userAtom } from "~/classes/user";
import { ClaimCategories, LocalStorageKey } from "~/classes/literals";
import {
	emptyStaticLookup,
	StaticLookup,
	staticLookupAtom,
} from "~/classes/staticLookup";
import { ApiServices } from "~/lib/api-services";
import { regionAtom, regionModalAtom, allowedRegionAtom } from "~/lib/jotai/region";
import { Regions } from "~/models/region";
import { RegionCode } from "~/types";
import { useErrorToast } from "~/hooks/customToasts";
import { RegionModal } from "~/components/region-modal";
import { REGION_TTL } from "~/models/generalConstants";
import { setItemWithExpiry } from "~/lib/utils";
import { Footer } from "~/components/footer";
import { ArrowUpRightIcon, CircleX } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { homeFormsFilterTabsTrigger } from "~/models/tabLists";
import { ScrollToElementButton } from "~/components/scroll-to-element";
import { captureRemixErrorBoundaryError } from "@sentry/remix";

export async function loader() {
	const lookup = { lookup: emptyStaticLookup, empty: true };

	const lookupVals: StaticLookup = await ApiServices.getStaticLookup();

	lookupVals.countries.sort((a, b) =>
		a.countryName.toLowerCase().localeCompare(b.countryName.toLowerCase())
	);

	lookup.empty = false;
	lookup.lookup = lookupVals;

	return {
		...lookup,
		legacyUrl: process.env.CLAIMS_LEGACY,
		allowedRegion: process.env.ALLOWED_REGION || null,
	};
}

export default function Index() {
	SaveUserInfo();
	const loaderData = useLoaderData<typeof loader>();
	const region: RegionCode = useAtomValue(regionAtom);
	const showRegionModal = useAtomValue(regionModalAtom);
	const { errorToast } = useErrorToast();
	const legacyurl = loaderData.legacyUrl;

	const setLookup = useSetAtom(staticLookupAtom);
	const setAllowedRegion = useSetAtom(allowedRegionAtom);
	useEffect(() => {
		if (loaderData.empty) {
			errorToast({
				description:
					"We're having trouble loading the dropdown menus for the forms.",
				duration: 10000,
			});
		}

		localStorage.setItem(
			LocalStorageKey.StaticLookup,
			JSON.stringify(loaderData.lookup)
		);

		setLookup(loaderData.lookup);
		// Always set allowedRegion to distinguish between undefined (not loaded) and null (no restriction)
		setAllowedRegion(loaderData.allowedRegion);
	}, [loaderData.allowedRegion, setAllowedRegion]);

	const forms: {
		jmValue: string;
		bbValue: string;
		name: string;
		image: string;
		category: ClaimCategories;
		description?: string;
	}[] = [
		{
			jmValue:
				"/report-a-claim/jm/motor/collision-no-other-vehicle/about-you",
			bbValue:
				"/report-a-claim/bb/motor/collision-no-other-vehicle/about-you",
			name: "Collision No Other Vehicle/s Involved",
			image: "collision-no-other-vehicle.png",
			category: ClaimCategories.Motor,
			description:
				"Covers accidents where only your vehicle is involved, like hitting a tree or barrier.",
		},
		{
			jmValue: "/report-a-claim/jm/motor/collision-other-vehicle/about-you",
			bbValue: "/report-a-claim/bb/motor/collision-other-vehicle/about-you",
			name: "Collision Other Vehicle/s Involved",
			image: "collision-with-other-vehicle.png",
			category: ClaimCategories.Motor,
			description:
				"Covers accidents where your vehicle collides with another vehicle, including sideswipes or head-on collisions.",
		},
		{
			jmValue: "/report-a-claim/jm/motor/fire/about-you",
			bbValue: "/report-a-claim/bb/motor/fire/about-you",
			name: "Fire",
			image: "fire.png",
			category: ClaimCategories.Motor,
			description:
				"Covers damage to your vehicle caused by fire, including accidents or external sources",
		},
		{
			jmValue: "/report-a-claim/jm/motor/theft/about-you",
			bbValue: "/report-a-claim/bb/motor/theft/about-you",
			name: "Theft",
			image: "theft.png",
			category: ClaimCategories.Motor,
			description: "Covers loss or damage if your vehicle is stolen.",
		},
		{
			jmValue: "/report-a-claim/jm/motor/windscreen/about-you",
			bbValue: "/report-a-claim/bb/motor/windscreen/about-you",
			name: "Windscreen",
			image: "windscreen.jpeg",
			category: ClaimCategories.GlassOnly,
			description:
				"Covers cracks or breakage of your vehicle's windscreen or other glass panels.",
		},
		{
			jmValue: `${legacyurl}?category=Motor&type=Lightning&region=ja`,
			bbValue: `${legacyurl}?category=Motor&type=Lightning&region=bb`,
			name: "Lightning",
			image: "lightning.png",
			category: ClaimCategories.Motor,
			description:
				"Covers damage to your vehicle caused by lightning strikes, including electrical and body damage.",
		},
		{
			jmValue: `${legacyurl}?category=Motor&type=Flood-Hurricane-Storm-etc.&region=ja`,
			bbValue: `${legacyurl}?category=Motor&type=Flood-Hurricane-Storm-etc.&region=bb`,
			name: "Flood, Hurricane, Storm etc.",
			image: "flood-hurricane-storm.png",
			category: ClaimCategories.Motor,
			description:
				"Covers damage caused by severe weather events, such as flooding, hurricanes, or storms.",
		},
	];

	const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
	React.useEffect(() => {
		setIsLoading(false);
	}, []);

	return (
		<>
			<div className="bg-companyGray relative z-[1]">
				{isLoading && <LoadingIndicator />}

				{showRegionModal && <RegionModal />}

				<div className="bg-white rounded-b-[2.5rem]">
					<div className="h-screen flex justify-center items-center flex-col gap-6 relative">
						<div className="relative">
							<h1 className="pt-0 px-2 font-black text-center text-7xl leading-[0.8] text-companyRed relative z-[2] sm:pt-16">
								We&apos;ve got <br /> you covered
							</h1>
						</div>
						<ScrollToElementButton
							elementId="home-forms-tabs"
							text="Report your claim"
						/>

						<div
							className="w-[13%] p-1 border-[2px] border-[#D0D0D0] border-dashed rounded-2xl absolute top-[51%] left-[5%] scale-0 delay-75 animate-scale duration-300 min-w-[16%] sm:min-w-[15%] md:min-w-0"
							aria-hidden="true">
							<img
								src="/home-flood.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
						<div
							className="w-[10%] p-1 border-[2px] border-[#D0D0D0] border-dashed rounded-2xl absolute top-[20%] left-[12%] scale-0 animate-scale min-w-[17%] sm:min-w-[14%] md:min-w-0 sm:top-[30%]"
							aria-hidden="true">
							<img
								src="/home-windscreen.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
						<div
							className="w-[20%] p-[0.3rem] bg-white rounded-2xl absolute top-[2%] left-[20%] scale-0 delay-100 animate-scale min-w-[20%] sm:min-w-[15%] md:min-w-0"
							aria-hidden="true">
							<img
								src="/home-collision.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
						<div
							className="w-[5%] h-[3%] bg-[#ffd00020] rounded-full z-[3] absolute top-[35%] left-[-5%] backdrop-blur-sm border-[1px] border-[#ebebeb] scale-0 delay-150 animate-scale duration-300 min-[380px]:top-[38%] min-[400px]:left-[5%] min-[560px]:h-[7%] min-w-[15%] sm:top-[29%] sm:min-w-[13%] lg:min-w-0 sm:left-[36%] "
							aria-hidden="true"></div>
						<div
							className="w-[10%] p-1 border-[2px] border-[#D0D0D0] border-dashed rounded-2xl absolute top-[8%] right-[25%] scale-0 duration-200 delay-200 animate-scale min-w-[14%] sm:min-w-[12%] md:min-w-0"
							aria-hidden="true">
							<img
								src="/home-mirror.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
						<div
							className="w-[13%] p-[0.3rem] bg-white rounded-2xl absolute top-[20%] right-[15%] scale-0 delay animate-scale min-w-[20%] sm:min-w-[15%] md:min-w-0"
							aria-hidden="true">
							<img
								src="/home-fire.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
						<div
							className="w-[10%] p-1 border-[2px] border-[#D0D0D0] border-dashed rounded-2xl absolute top-[54%] right-[6%] scale-0 delay-200 duration-300 animate-scale min-w-[14%] sm:min-w-[12%] md:min-w-0"
							aria-hidden="true">
							<img
								src="/home-damaged-car.jpg"
								draggable="false"
								alt=""
								className="rounded-xl"
							/>
						</div>
					</div>

					<Tabs
						defaultValue={ClaimCategories.Motor}
						className="w-full flex flex-col items-center">
						<TabsList asChild>
							<div
								id="home-forms-tabs"
								className="shadow-[0_0_15px_rgba(0,0,0,0.1)] overflow-clip border-[1.5px] border-white bg-[#DEDEDE0A] rounded-xl p-3 h-[4.5rem] w-fit relative">
								<div className="bottom-[-30%] right-[-5%] z-0 blur-xl rounded-full w-20 h-20 absolute bg-companyRed/30"></div>
								<div className="top-[-90%] left-[-20%] z-0 blur-md rounded-full w-28 h-28 absolute bg-companyRed/70"></div>
								{homeFormsFilterTabsTrigger.map((tabInfo) => {
									return (
										<TabsTrigger
											key={tabInfo.label}
											value={tabInfo.value}
											className="group relative mx-2 border-[1.5px] px-5 py-3 border-transparent data-[state=active]:border-[rgb(243,243,243)] rounded-xl data-[state=active]:bg-yellow-500/35 text-gray-600 hover:text-companyBlack">
											<p>{tabInfo.label}</p>
											<div className="duration-300 group-data-[state=active]:scale-0 group-hover:scale-100 transition-all ease-in-out absolute bottom-0 right-[48%] scale-0 w-4 h-1 bg-companyRed/60 rounded-full origin-center"></div>
										</TabsTrigger>
									);
								})}
							</div>
						</TabsList>
						{Object.values(ClaimCategories).map((category) => {
							return (
								<TabsContent
									tabIndex={-1}
									key={category}
									value={category}
									className="pt-5 data-[state=inactive]:hidden"
									forceMount>
									<div className="mb-28 grid grid-cols-1 gap-7 py-3 px-5 sm:px-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
										{forms
											.filter((form) => form.category === category)
											.map((item) => {
												return (
													<Link
														key={item.name}
														to={
															region === Regions.Barbados.code
																? item.bbValue
																: item.jmValue
														}
														className="group"
														onClick={() => {
															setIsLoading(true);
														}}>
														<div className="h-full rounded-xl shadow-[0_2px_21.4px_rgba(26,26,26,0.2)] flex flex-col items-center relative scale-100 duration-300 ease-linear  transition-transform group-hover:scale-105">
															<div className="p-4">
																<div className="rounded-xl relative overflow-hidden">
																	<div className="top-[-10%] right-[-10%] z-[4] rounded-full w-[91%] h-[91%] absolute blur-xl bg-blue-300/50 mix-blend-color opacity-0 group-hover:opacity-100 transition-opacity ease-in-out duration-500 sm:top-[-80%] sm:right-0 sm:w-[130%] sm:h-[130%]"></div>
																	<div className="relative">
																		<div className="absolute w-full h-full z-[2] bg-amber-600/45 mix-blend-soft-light duration-500 transition-all ease-in-out group-hover:bg-red-600/70"></div>
																		<img
																			src={`/${item.image}`}
																			alt=""
																			className="rounded-xl contrast-125 group-hover:contrast-[140%] group-hover:scale-[1.15] duration-500 transition-all ease-in-out"
																		/>
																	</div>
																</div>
																<h2 className="font-medium leading-tight pt-6 duration-300 transition-all ease-in-out group-hover:text-companyRed">
																	{item.name}
																</h2>

																<p className="pt-2 text-[0.8rem] text-slate-400">
																	{item.description}
																</p>

																<div className="mb-[3.5rem]"></div>
															</div>
															<div className="absolute bottom-0 right-0 p-5  rounded-br-xl rounded-tl-[3rem] duration-300 transition-all ease-in-out bg-companyRed group-hover:bg-companyBlack">
																<ArrowUpRightIcon className="text-white rotate-0 duration-300 transition-all ease-in-out group-hover:rotate-45" />
															</div>
														</div>
													</Link>
												);
											})}
									</div>
								</TabsContent>
							);
						})}
					</Tabs>
				</div>
			</div>
			<Footer />
		</>
	);
}

function SaveUserInfo() {
	const setRegion = useSetAtom(regionAtom);
	const setShowRegionModal = useSetAtom(regionModalAtom);

	const [searchParams] = useSearchParams();
	const u = searchParams.get("u");

	let usr: User;
	if (u) {
		usr = JSON.parse(atob(decodeURI(u)));
	}

	const setUser = useSetAtom(userAtom);
	useEffect(() => {
		const result = usr ?? emptyUser;
		localStorage.setItem(LocalStorageKey.U, JSON.stringify(result));
		setUser(result);

		if (
			result?.Location?.Country != null &&
			Object.values(Regions).some(
				(region) =>
					region.name.toLowerCase() ===
					result.Location.Country.toLowerCase()
			)
		) {
			const region = Object.values(Regions).filter(
				(region) =>
					region.name.toLowerCase() ===
					result.Location.Country.toLowerCase()
			);

			if (region.length > 0) {
				setRegion(region[0].code);
				setItemWithExpiry(
					LocalStorageKey.Country,
					region[0].name,
					REGION_TTL,
					() => {
						setShowRegionModal(false);
					}
				);
			}
		}
	}, []);
}

export function ErrorBoundary() {
	const error = useRouteError();
	captureRemixErrorBoundaryError(error);

	return (
		<div className="flex flex-col p-8 leading-[0.5rem] text-sm text-companyRed justify-center items-center">
			<CircleX className="mb-2" size={32} strokeWidth={3} />
			<p className="font-bold text-lg">Oops!</p>
			<p className="text-center max-w-[25%] leading-tight">
				We&apos;re having trouble loading the page. Please try reloading or
				contacting the site administrator.
			</p>
		</div>
	);
}
